import { useState } from 'react';
import { Button } from '../../components/Button.jsx';
import { Icon } from '../../components/Icon.jsx';
import { RestaurantImage } from '../../components/RestaurantImage.jsx';
import { Sheet } from '../../components/Sheet.jsx';
import { getInitials } from '../../lib/identity.js';
import { color, font, radius, overline } from '../../theme/tokens.js';

/** Bar colour by finishing position. */
const RANK_COLORS = [color.primary, color.warning, color.star];

/**
 * Live leaderboard for a group session.
 *
 * Each row expands to show who voted and what time they wanted, because the
 * interesting question in practice isn't just "which restaurant won" but
 * "which slot works for the people who picked it".
 *
 * Every right swipe appears immediately. Completion is tracked only so the
 * group can close itself after everyone has finished the shared ballot.
 *
 * @param {object} props
 * @param {ReturnType<typeof import('./useGroupSession.js').useGroupSession>['leaderboard']} props.leaderboard
 * @param {number} props.memberCount
 * @param {number} props.submittedCount
 * @param {boolean} props.isEnded
 * @param {(() => void) | null} [props.onEnd]  Set for whoever started the group.
 * @param {() => void} props.onClose
 */
export function GroupResultsSheet({
  leaderboard,
  memberCount,
  submittedCount,
  myVotes,
  isEnded,
  onEnd = null,
  onClose,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const winner = leaderboard[0];

  const heading = isEnded
    ? winner
      ? `The group picked ${winner.restaurant.name}`
      : 'The group has ended'
    : leaderboard.length > 0
      ? `${winner.restaurant.name} is leading`
      : 'No right swipes yet';

  return (
    <Sheet onDismiss={onClose} zIndex={115} maxHeight="84vh" padded={false}>
      <div
        style={{
          flexShrink: 0,
          padding: '0 20px 10px',
          borderBottom: `1px solid ${color.border}`,
        }}
      >
        <p style={{ ...overline, marginBottom: 3 }}>
          {isEnded ? 'Final results' : 'Group results'} · {submittedCount} of {memberCount} finished
        </p>
        <h2
          style={{
            margin: 0,
            fontSize: 19,
            fontWeight: font.weight.heavy,
            letterSpacing: '-0.4px',
            color: color.ink,
          }}
        >
          {heading}
        </h2>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 12px' }}>
        {leaderboard.length === 0 && (
          <div style={{ textAlign: 'center', padding: '44px 0' }}>
            <Icon name="users" size={30} stroke={color.inkFaint} />
            <p style={{ margin: '12px 0 0', fontSize: 14, color: color.inkFaint }}>
              {isEnded
                ? 'Nobody swiped right before the group ended.'
                : 'Right swipes appear here live.'}
            </p>
          </div>
        )}

        {leaderboard.map((entry, position) => (
          <LeaderboardRow
            key={entry.restaurant.id}
            entry={entry}
            position={position}
            memberCount={memberCount}
            isMyPick={myVotes[entry.restaurant.id] === 'right'}
            expanded={expandedId === entry.restaurant.id}
            onToggle={() =>
              setExpandedId(expandedId === entry.restaurant.id ? null : entry.restaurant.id)
            }
          />
        ))}
      </div>

      <div
        style={{
          flexShrink: 0,
          padding: '12px 20px 36px',
          borderTop: `1px solid ${color.border}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {isEnded ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            {onEnd && (
              <Button
                variant="secondary"
                onClick={onEnd}
                style={{ padding: '12px 20px', fontSize: 14, color: color.danger }}
              >
                End group now
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={onClose}
              style={{ padding: '12px 20px', fontSize: 14 }}
            >
              Keep swiping
            </Button>
          </>
        )}
      </div>
    </Sheet>
  );
}

function LeaderboardRow({ entry, position, memberCount, isMyPick, expanded, onToggle }) {
  const { restaurant, votes, voters, bestTime } = entry;
  const isWinner = position === 0;
  const share = memberCount > 0 ? Math.round((votes / memberCount) * 100) : 0;

  return (
    <div style={{ borderBottom: `1px solid ${color.border}` }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          gap: 11,
          alignItems: 'center',
          padding: '11px 0',
          cursor: 'pointer',
        }}
      >
        <div style={{ width: 26, textAlign: 'center', flexShrink: 0 }}>
          {isWinner ? (
            <Icon name="trophy" size={18} stroke={color.primary} />
          ) : (
            <span style={{ fontSize: 13, fontWeight: font.weight.bold, color: color.inkFaint }}>
              {position + 1}
            </span>
          )}
        </div>

        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: radius.md,
            overflow: 'hidden',
            flexShrink: 0,
            border: `1.5px solid ${isWinner ? color.primary : color.border}`,
          }}
        >
          <RestaurantImage restaurant={restaurant} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 2,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  flex: '0 1 auto',
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: isWinner ? font.weight.heavy : font.weight.semibold,
                  color: color.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {restaurant.name}
              </p>
              {isMyPick && (
                <span
                  style={{
                    flexShrink: 0,
                    padding: '2px 5px',
                    borderRadius: radius.sm,
                    background: color.primarySoft,
                    color: color.primaryDark,
                    fontSize: 8,
                    fontWeight: font.weight.heavy,
                  }}
                >
                  YOU
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: font.weight.heavy,
                  color: isWinner ? color.primary : color.inkFaint,
                }}
              >
                {votes}/{memberCount}
              </span>
              <span style={{ fontSize: 10, color: color.inkFaint }}>{expanded ? '▲' : '▼'}</span>
            </div>
          </div>

          <p style={{ margin: '0 0 5px', fontSize: 10, color: color.inkFaint }}>
            {restaurant.cuisine} · {restaurant.neighborhood}
          </p>

          <div
            style={{
              height: 5,
              borderRadius: 3,
              background: color.surfaceSunken,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${share}%`,
                borderRadius: 3,
                background: RANK_COLORS[position] ?? color.borderStrong,
                transition: 'width 0.5s cubic-bezier(0.175,0.885,0.32,1.275)',
              }}
            />
          </div>

          {bestTime && (
            <p
              style={{
                margin: '3px 0 0',
                fontSize: 9,
                fontWeight: font.weight.bold,
                color: color.success,
              }}
            >
              Best time: {bestTime}
            </p>
          )}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginBottom: 10,
            padding: '10px 12px',
            borderRadius: radius.md,
            background: color.surfaceAlt,
          }}
        >
          {voters.map((voter, position2) => (
            <div
              key={voter.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '5px 0',
                borderBottom: position2 < voters.length - 1 ? `1px solid ${color.border}` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: color.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ color: color.inkInverse, fontSize: 10, fontWeight: font.weight.heavy }}
                  >
                    {getInitials(voter.name)}
                  </span>
                </div>
                <span style={{ fontSize: 12, fontWeight: font.weight.semibold, color: color.ink }}>
                  {voter.name}
                </span>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: voter.time ? color.success : color.inkFaint,
                  fontWeight: voter.time ? font.weight.bold : font.weight.regular,
                }}
              >
                {voter.time ?? 'No preference'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
